import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { path } from 'ramda';
import { useMutation, useApolloClient } from '@apollo/client';
import { useManagedQuery } from 'utils/managedHooks';
import { getBookingJourneysURL, getClubEurostarSubscriptionURL, getPaymentsURL } from 'utils/urls';
import { FullPageSpinner } from 'admin-components/components';

import { ADD_PAYMENT, UPDATE_PAYMENTS, TAKE_DEPOSIT, PROCESS_CARD_REFUND, ADD_PAYOUT_AND_BALANCE } from 'store/mutations';
import AgentPortalError from 'errors/AgentPortalError';

import { bookingShape, historyShape, paymentsShape, legShape, passengersShape } from 'constants/propTypes';
import { ACTION_CODES } from 'constants/errors';
import { codes } from 'constants/payment';

import { withUser } from 'components/queries/GetUserQuery';
import GetPaymentMethodsQuery from 'components/queries/GetPaymentMethodsQuery';
import PayAndConfirmMutation from 'components/mutations/PayAndConfirmMutation';
import { GET_BOOKING } from 'store/queries';
import { isVoucherPayment, isSubscriptionOnly } from 'utils/payment';
import { hasTrainSegment } from 'pages/BookingDetails/Extras/Extras.utils';
import Payments from './Payments';
import Refunds from './Refunds';
import CancelVoucherRefunds from './CancelVoucherRefunds';
import { isBookingEHL } from './Payments.utils';
import CREATE_PAYMENT_LINK from 'store/mutations/CREATE_PAYMENT_LINK';
import { isNewAriaBooking } from '../../../utils/booking';
import { bookingStatus } from 'types/booking';

const Index = ({
  reference,
  currency,
  status,
  totalPriceToBePaid,
  payments,
  refunds,
  customer,
  amountDue,
  depositAmount,
  type = 'payment',
  history,
  cancelPaymentsLoading,
  cancelPayments,
  refetchBooking,
  isLoyalty,
  ...rest
}) => {
  const client = useApolloClient();
  const [isAssociatedBookingEHL, setIsAssociatedBookingEHL] = useState(false);
  const isRefund = history.location.pathname.includes('refund-methods');
  const isCancelVoucherRefundRoute = isRefund && history.location.search.includes('cancel-voucher');
  const membershipNumber = new URLSearchParams(history.location.search).get('membershipNumber');
  const subscriptionID = new URLSearchParams(history.location.search).get('subscriptionID');
  const subscriptionCode = new URLSearchParams(history.location.search).get('subscriptionCode');
  const isSubscription = new URLSearchParams(history.location.search).get('isSubscription') === 'true';
  const isRefundingThalysPass = new URLSearchParams(history.location.search).get('isRefundingThalysPass') === 'true';
  const redirectToSubscriptions = type === 'refund' && membershipNumber && isRefundingThalysPass;

  const subscriptionUrl = membershipNumber && isSubscription ? getClubEurostarSubscriptionURL(membershipNumber, reference) : null;

  const {
    data: dataBooking,
    loading: isBookingLoading,
    refetch
  } = useManagedQuery(GET_BOOKING, {
    variables: { reference, currency },
    skip: !reference
  });

  const isAriaBooking = isNewAriaBooking(dataBooking?.booking);

  const isExtrasOnlyBooking =
    !hasTrainSegment(dataBooking?.booking) &&
    Boolean(dataBooking?.booking?.outboundExtras?.length || dataBooking?.booking?.inboundExtras?.length);

  const isSubscriptionOnlyBooking = dataBooking?.booking && isSubscriptionOnly(dataBooking.booking);

  useEffect(() => {
    const handleAssociatedMainBooking = async () => {
      const extraOnlyParentBookingReference = dataBooking?.booking?.extraOnlyParentBookingReference;

      if (!extraOnlyParentBookingReference) {
        setIsAssociatedBookingEHL(false);
      } else {
        try {
          const {
            data: {
              booking: { additionalDetails }
            }
          } = await client.query({
            query: GET_BOOKING,
            variables: { reference: extraOnlyParentBookingReference, currency }
          });

          setIsAssociatedBookingEHL(isBookingEHL(additionalDetails));
        } catch {
          setIsAssociatedBookingEHL(false);
        }
      }
    };

    if (isExtrasOnlyBooking) {
      handleAssociatedMainBooking();
    }
  }, [client, currency, dataBooking?.booking?.extraOnlyParentBookingReference, isExtrasOnlyBooking]);

  const { pathname: paymentUrlPathname, search: paymentUrlQueryString } = rest.location;
  const paymentUrlPrefix = `${paymentUrlPathname}${paymentUrlQueryString}`;

  const TYPES = {
    payment: Payments,
    refund: Refunds
  };

  const Component = TYPES[type];

  const [addPaymentMutation, { loading: addPaymentLoading, error }] = useMutation(ADD_PAYMENT, {
    refetchQueries: ['booking'],
    awaitRefetchQueries: true
  });

  const addPayment = (mutationSettings) => {
    const { variables } = mutationSettings;
    const paymentsToAdd = variables.payments.filter((payment) => !isVoucherPayment(payment));

    if (paymentsToAdd.length) {
      return addPaymentMutation({
        ...mutationSettings,
        variables: {
          ...variables,
          payments: paymentsToAdd
        }
      });
    }

    return refetchBooking ? refetchBooking() : refetch();
  };

  const [addPayoutAndBalance, { loading: addPayoutAndBalanceLoading, error: addPayoutAndBalanceError }] = useMutation(
    ADD_PAYOUT_AND_BALANCE,
    {
      refetchQueries: ['booking'],
      awaitRefetchQueries: true
    }
  );

  const [takeDeposit, { loading: takeDepositLoading, error: takeDepositError }] = useMutation(TAKE_DEPOSIT, {
    onCompleted: () => history.push(getBookingJourneysURL(reference, membershipNumber, subscriptionID, subscriptionCode))
  });

  const [updatePayments, { loading: updatePaymentsLoading, error: updatePaymentsError }] = useMutation(UPDATE_PAYMENTS, {
    onCompleted: () =>
      redirectToSubscriptions
        ? history.push(getClubEurostarSubscriptionURL(membershipNumber, reference), {
            shouldRetry: true
          })
        : history.push(getBookingJourneysURL(reference, membershipNumber, subscriptionID, subscriptionCode))
  });

  const [createPaymentLink, { loading: createPaymentLinkLoading, error: createPaymentLinkError }] = useMutation(CREATE_PAYMENT_LINK, {
    onCompleted: () => history.push(getPaymentsURL(reference, membershipNumber))
  });

  const [processCardPaymentRefund, { loading: processCardPaymentRefundLoading, error: processCardPaymentRefundError }] = useMutation(
    PROCESS_CARD_REFUND,
    {
      refetchQueries: ['booking'],
      awaitRefetchQueries: true,
      onCompleted: ({ processCardPaymentRefund: { paymentsRefundBalance } }) => {
        if (paymentsRefundBalance === 0) {
          return redirectToSubscriptions
            ? history.push(getClubEurostarSubscriptionURL(membershipNumber, reference), {
                shouldRetry: true
              })
            : history.push(getBookingJourneysURL(reference, membershipNumber, subscriptionID, subscriptionCode));
        }
      }
    }
  );

  const loading =
    addPaymentLoading ||
    addPayoutAndBalanceLoading ||
    takeDepositLoading ||
    processCardPaymentRefundLoading ||
    cancelPaymentsLoading ||
    createPaymentLinkLoading ||
    isBookingLoading;

  if (error) {
    throw new AgentPortalError(error, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: `${paymentUrlPrefix}`
    });
  }

  if (addPayoutAndBalanceError) {
    throw new AgentPortalError(addPayoutAndBalanceError, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: `${paymentUrlPrefix}`
    });
  }

  if (takeDepositError) {
    throw new AgentPortalError(takeDepositError, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: `${paymentUrlPrefix}`
    });
  }

  if (updatePaymentsError) {
    throw new AgentPortalError(updatePaymentsError, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: `${paymentUrlPrefix}`
    });
  }

  if (processCardPaymentRefundError) {
    throw new AgentPortalError(processCardPaymentRefundError, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: `${paymentUrlPrefix}`
    });
  }

  if (createPaymentLinkError) {
    throw new AgentPortalError(createPaymentLinkError, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: `${paymentUrlPrefix}`
    });
  }

  if (loading) {
    return <FullPageSpinner />;
  }

  let isRefundingEntireBlueVoucherBooking =
    payments.filter((payment) => payment.method === codes.BLUE_VOUCHER && payment.reference?.split('-').length === 2).length === 1;

  isRefundingEntireBlueVoucherBooking =
    isRefundingEntireBlueVoucherBooking && payments.filter((payment) => payment.method === codes.REFUND).length === 1;

  isRefundingEntireBlueVoucherBooking =
    isRefundingEntireBlueVoucherBooking && dataBooking?.booking?.allBookingItems.every((item) => item.cancelled);
  if (isCancelVoucherRefundRoute) {
    return (
      <GetPaymentMethodsQuery reference={reference} isRefund={isRefund}>
        {({ data }) => (
          <CancelVoucherRefunds
            reference={reference}
            currency={currency}
            customer={customer}
            status={status}
            totalPriceToBePaid={totalPriceToBePaid}
            payments={payments}
            refunds={refunds}
            amountDue={amountDue}
            depositAmount={depositAmount}
            availableMethods={path(['paymentMethods'], data)}
            addPayment={addPayment}
            addPayoutAndBalance={addPayoutAndBalance}
            cancelPayments={cancelPayments}
            takeDeposit={takeDeposit}
            updatePayments={updatePayments}
            processCardPaymentRefund={processCardPaymentRefund}
            loading={!!(loading || updatePaymentsLoading)}
            type={type}
            history={history}
            booking={dataBooking.booking}
            {...rest}
          />
        )}
      </GetPaymentMethodsQuery>
    );
  }

  return (
    <GetPaymentMethodsQuery reference={reference} isRefund={isRefund}>
      {({ data }) => (
        <PayAndConfirmMutation
          bookingReference={reference}
          paymentUrlPrefix={paymentUrlPrefix}
          booking={dataBooking.booking}
          subscriptionUrl={subscriptionUrl}
        >
          {(payAndConfirm, { loading: payAndConfirmLoading }) => (
            <Component
              reference={reference}
              currency={currency}
              customer={customer}
              status={status}
              totalPriceToBePaid={totalPriceToBePaid}
              payments={payments}
              refunds={refunds}
              amountDue={amountDue}
              depositAmount={depositAmount}
              availableMethods={isSubscriptionOnlyBooking ? [{ code: 'NONE_CC' }, { code: 'NONE_BACS' }] : path(['paymentMethods'], data)}
              addPayment={addPayment}
              addPayoutAndBalance={addPayoutAndBalance}
              createPaymentLink={createPaymentLink}
              cancelPayments={cancelPayments}
              payAndConfirm={payAndConfirm}
              takeDeposit={takeDeposit}
              updatePayments={updatePayments}
              processCardPaymentRefund={processCardPaymentRefund}
              loading={!!(loading || payAndConfirmLoading || updatePaymentsLoading)}
              isAssociatedBookingEHL={isAssociatedBookingEHL}
              paymentUrlPrefix={paymentUrlPrefix}
              type={type}
              history={history}
              {...rest}
              isLoyalty={isLoyalty}
              pendingCancellationLegs={dataBooking?.booking?.pendingCancellationLegs}
              isInAfterSales={
                dataBooking?.booking?.isInAfterSales || [bookingStatus.PENDING_CHANGES].includes(dataBooking?.booking?.status)
              }
              outbound={dataBooking?.booking?.outbound}
              inbound={dataBooking?.booking?.inbound}
              isAriaBooking={isAriaBooking}
              isRefundingEntireBlueVoucherBooking={isRefundingEntireBlueVoucherBooking}
              isGroup={dataBooking?.booking?.isGroupBooking}
              totalPrice={dataBooking?.booking?.totalPrice}
              changeExpiryTimestamp={dataBooking?.booking?.changeExpiryTimestamp}
              additionalDetails={dataBooking?.booking?.additionalDetails}
            />
          )}
        </PayAndConfirmMutation>
      )}
    </GetPaymentMethodsQuery>
  );
};

Index.propTypes = {
  reference: PropTypes.string,
  type: PropTypes.oneOf(['payment', 'refund']),
  bookingData: bookingShape,
  refetchBooking: PropTypes.func,
  history: historyShape,
  currency: PropTypes.string,
  status: PropTypes.string,
  totalPriceToBePaid: PropTypes.number,
  customer: PropTypes.shape({
    email: PropTypes.string
  }),
  amountDue: PropTypes.shape({
    amount: PropTypes.number,
    currency: PropTypes.string
  }),
  depositAmount: PropTypes.number,
  payments: paymentsShape,
  refunds: paymentsShape,
  cancelPayments: PropTypes.func,
  cancelPaymentsLoading: PropTypes.bool,
  outbound: PropTypes.arrayOf(legShape),
  inbound: PropTypes.arrayOf(legShape),
  passengers: passengersShape,
  isLoyalty: PropTypes.bool
};

export default withRouter(withUser(Index));
